import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, PenLine, Gift, Mail, Facebook, Twitter, Link2, Check } from "lucide-react";
import { BOOK } from "@/lib/site";

const SHARE_TEXT =
  "Pre-order Hannah's Heart Awakens — a children's story about discovering Jesus' love. Out July 24, 2026!";

const buttons = [
  { to: "#launch", icon: BookOpen, label: "Shop the Book", type: "anchor" },
  { to: "/blog", icon: PenLine, label: "Read the Blog", type: "route" },
  { to: "/resources", icon: Gift, label: "Free Resources", type: "route" },
  { to: "/contact", icon: Mail, label: "Contact", type: "route" },
];

export default function SpreadTheWord() {
  const [copied, setCopied] = useState(false);
  const url = BOOK.siteUrl;
  const facebookShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const twitterShare = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(SHARE_TEXT)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied! Paste it anywhere to share.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy the link manually.");
    }
  };

  return (
    <section id="welcome" className="relative z-10 py-24 md:py-32" data-testid="welcome-hub">
      <div className="mx-auto max-w-5xl px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 text-center backdrop-blur-sm md:p-14"
        >
          <span className="text-3xl text-gold">✦</span>
          <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-cream md:text-5xl">
            Welcome to Hannah Series Official
          </h2>
          <p className="mx-auto mt-4 max-w-2xl font-body text-lg leading-relaxed text-slate-300">
            Stories that point children to Jesus—one story, one prayer, and one heart at a time.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {buttons.map((b, i) => {
              const inner = (
                <>
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-gold/15 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-twilight">
                    <b.icon className="h-5 w-5" />
                  </div>
                  <span className="mt-4 font-display text-lg font-semibold text-cream">{b.label}</span>
                </>
              );
              const cls =
                "group flex flex-col items-center rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-gold/40";
              return (
                <motion.div
                  key={b.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                >
                  {b.type === "anchor" ? (
                    <a href={b.to} data-testid={`hub-${b.label.toLowerCase().replace(/\s/g, "-")}`} className={cls}>
                      {inner}
                    </a>
                  ) : (
                    <Link to={b.to} data-testid={`hub-${b.label.toLowerCase().replace(/\s/g, "-")}`} className={cls}>
                      {inner}
                    </Link>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Share row */}
          <div className="mt-12 border-t border-white/10 pt-8">
            <p className="font-body text-sm text-slate-400">Share this with a family who needs it:</p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <a
                href={facebookShare}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-facebook"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 font-body text-sm text-cream transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
              >
                <Facebook className="h-4 w-4" /> Facebook
              </a>
              <a
                href={twitterShare}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-twitter"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 font-body text-sm text-cream transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
              >
                <Twitter className="h-4 w-4" /> X
              </a>
              <button
                onClick={copyLink}
                data-testid="share-copy-link"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 font-body text-sm text-cream transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
              >
                {copied ? <Check className="h-4 w-4 text-gold" /> : <Link2 className="h-4 w-4" />}
                {copied ? "Copied!" : "Copy link"}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
